/**
 * 영어 화면 자동 번역 (2026-10-11 글로벌). 화면에 보이는 한국어 문구를 사전(src/i18n/en.json)으로 바꾼다.
 * - 사전은 tools/i18n/extract.py 로 소스의 문구를 뽑아 번역한 것 (문구가 바뀌면 다시 뽑아 번역 추가)
 * - 글자 그대로 같은 문구만 바꾸고, `${...}` 가 들어간 문구는 그 자리를 그대로 옮긴다
 * - 가사·곡 제목 등 바꾸면 안 되는 곳은 data-nt 를 붙인 요소 안을 건너뛴다
 * 영어 화면(isEn)일 때만 사전을 내려받아 켠다.
 */
type Dict = { [ko: string]: string };

let exact: Map<string, string> | null = null;
let patterns: { re: RegExp; names: string[]; en: string }[] = [];
const ATTRS = ['placeholder', 'aria-label', 'title', 'alt'];

const escapeRe = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const build = (dict: Dict) => {
  exact = new Map();
  patterns = [];
  Object.keys(dict).forEach((ko) => {
    const en = dict[ko];
    if (!en || en === ko) return;
    if (ko.includes('${')) {
      const names: string[] = [];
      const parts = ko.split(/\$\{([^}]*)\}/);
      let src = '^';
      parts.forEach((p, i) => {
        if (i % 2) {
          names.push(p);
          src += '(.+?)';
        } else src += escapeRe(p);
      });
      patterns.push({ re: new RegExp(src + '$'), names, en });
    } else {
      exact!.set(ko, en);
    }
  });
};

export const translate = (text: string): string => {
  if (!exact || !/[가-힣]/.test(text)) return text;
  const t = text.replace(/\s+/g, ' ').trim();
  const hit = exact.get(t);
  if (hit) return (text.match(/^\s*/) || [''])[0] + hit + (text.match(/\s*$/) || [''])[0];
  for (const p of patterns) {
    const m = t.match(p.re);
    if (m) {
      let en = p.en;
      p.names.forEach((n, i) => {
        en = en.split('${' + n + '}').join(translate(m[i + 1])); // 끼워 넣은 문구도 사전에 있으면 영어로
      });
      return en;
    }
  }
  return text;
};

const skip = (el: Element | null) => !!(el && el.closest && el.closest('[data-nt], script, style, textarea, input'));

const doText = (n: Node) => {
  const v = n.nodeValue;
  if (!v || !/[가-힣]/.test(v) || skip(n.parentElement)) return;
  const t = translate(v);
  if (t !== v) n.nodeValue = t;
};

const doEl = (el: Element) => {
  if (skip(el)) return;
  ATTRS.forEach((a) => {
    const v = el.getAttribute(a);
    if (v && /[가-힣]/.test(v)) {
      const t = translate(v);
      if (t !== v) el.setAttribute(a, t);
    }
  });
};

const walk = (root: Node) => {
  if (root.nodeType === 3) return doText(root);
  if (root.nodeType !== 1) return;
  const el = root as Element;
  if (skip(el)) return;
  doEl(el);
  const tw = document.createTreeWalker(el, NodeFilter.SHOW_TEXT | NodeFilter.SHOW_ELEMENT);
  let n: Node | null = tw.nextNode();
  while (n) {
    if (n.nodeType === 3) doText(n);
    else doEl(n as Element);
    n = tw.nextNode();
  }
};

export const startAutoTranslate = async () => {
  try {
    const mod: any = await import('../i18n/en.json');
    build(mod.default || mod);
  } catch (e) {
    return;
  }
  walk(document.body);
  if (document.title) document.title = translate(document.title);
  const mo = new MutationObserver((ms) => {
    ms.forEach((m) => {
      if (m.type === 'characterData') doText(m.target);
      else if (m.type === 'attributes') doEl(m.target as Element);
      else m.addedNodes.forEach(walk);
    });
  });
  mo.observe(document.body, { childList: true, subtree: true, characterData: true, attributes: true, attributeFilter: ATTRS });
  // 경고·확인 창 문구도
  const a = window.alert.bind(window);
  window.alert = (msg?: any) => a(typeof msg === 'string' ? translate(msg) : msg);
  const c = window.confirm.bind(window);
  window.confirm = (msg?: string) => c(typeof msg === 'string' ? translate(msg) : msg);
};
